import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Package, Edit, Trash2, Eye, Plus } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import DataTable from '../../components/admin/DataTable';
import LoadingState from '../../components/admin/LoadingState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';

const Products: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{isOpen: boolean, product: any}>({
    isOpen: false,
    product: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Gestion des Produits | Admin';
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product: any) => {
    navigate(`/admin/products/edit/${product.id}`);
  };

  const handleView = (product: any) => {
    window.open(`/products/${product.slug}`, '_blank');
  };

  const handleDelete = async () => {
    if (!deleteConfirm.product) return;
    
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', deleteConfirm.product.id);

      if (error) throw error;
      
      setProducts(products.filter(p => p.id !== deleteConfirm.product.id));
      toast.success('Produit supprimé avec succès');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erreur lors de la suppression du produit');
    } finally {
      setDeleteConfirm({ isOpen: false, product: null });
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Nom',
      render: (value: string, item: any) => (
        <div className="flex items-center">
          <div className="w-10 h-10 flex-shrink-0 mr-3">
            {item.images && item.images.length > 0 ? (
              <img 
                src={item.images[0]} 
                alt={value}
                className="w-10 h-10 rounded-md object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-md bg-primary-100 flex items-center justify-center">
                <Package size={20} className="text-primary-600" />
              </div>
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-xs text-gray-500">{item.model}</div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'type',
      header: 'Type',
      render: (value: string) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
          {value}
        </span>
      ),
      sortable: true,
      width: 'w-24'
    },
    {
      key: 'price',
      header: 'Prix',
      render: (value: number) => formatCurrency(value),
      sortable: true,
      width: 'w-32'
    },
    {
      key: 'created_at',
      header: 'Créé le',
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR'),
      sortable: true,
      width: 'w-32'
    }
  ];

  return (
    <div className="px-6">
      <PageHeader
        title="Produits"
        description="Gérez les produits de votre catalogue"
        actionLink="/admin/products/new"
        actionLabel="Ajouter un produit"
        actionIcon={<Plus size={16} />}
      />
      
      {loading ? (
        <LoadingState type="table" />
      ) : (
        <DataTable
          columns={columns}
          data={products}
          onEdit={handleEdit}
          onDelete={(product) => setDeleteConfirm({ isOpen: true, product })}
          onView={handleView}
          searchPlaceholder="Rechercher un produit..."
          searchKey="name"
          emptyTitle="Aucun produit"
          emptyDescription="Vous n'avez pas encore ajouté de produit."
          emptyActionLabel="Ajouter un produit"
          onEmptyAction={() => navigate('/admin/products/new')}
        />
      )}
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Supprimer le produit"
        message={`Êtes-vous sûr de vouloir supprimer le produit "${deleteConfirm.product?.name}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, product: null })}
        type="danger"
      />
    </div>
  );
};

export default Products;